<template>
    <div class="users-container">
        <!-- Filtros Avançados -->
        <AdvancedFilters 
            ref="advancedFilters"
            :show-date-filter="true"
            :show-course-filter="true"
            :show-user-filter="true"
            :show-status-filter="false"
            :show-score-filter="true"
            @filters-applied="onFiltersApplied"
            @filters-cleared="onFiltersCleared"
        />

        <Loader v-if="!hasReady" />
        <div v-else>
            <v-table class="custom-table table-with-filters">
                <thead>
                    <tr>
                        <th style="width: 30%; text-align: left;" class="sortable-header" @click="sortBy('title')">
                            {{ $t('admin.reports.columns.quizTitle') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 20%; text-align: left;" class="sortable-header" @click="sortBy('courseName')">
                            {{ $t('admin.reports.columns.course') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 20%; text-align: left;" class="sortable-header" @click="sortBy('userName')">
                            {{ $t('admin.reports.columns.student') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 15%; text-align: center;" class="sortable-header" @click="sortBy('score')">
                            {{ $t('admin.reports.columns.score') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 10%; text-align: center;" class="sortable-header" @click="sortBy('date')">
                            {{ $t('admin.reports.columns.date') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 10%; text-align: center;">
                            {{ $t('admin.reports.columns.actions') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="quiz in paginatedQuizzes" :key="quiz.id">
                        <td style="width: 30%; text-align: left;">{{ quiz.quizTitle || quiz.title || this.$t('admin.reports.fallbacks.noTitle') }}</td>
                        <td style="width: 20%; text-align: left;">{{ quiz.courseName || 'N/A' }}</td>
                        <td style="width: 20%; text-align: left;">{{ quiz.userName || 'N/A' }}</td>
                        <td style="width: 15%; text-align: center;">
                            <v-chip
                                density="compact"
                                :color="getScoreColor(quiz.score)"
                            >
                                {{ quiz.score ? (quiz.score / 10).toFixed(1) : 'N/A' }}
                            </v-chip>
                        </td>
                        <td style="width: 10%; text-align: center;">{{ formatDate(quiz.dateInsert) }}</td>
                        <td style="width: 10%; text-align: center;">
                            <div class="action-buttons">
                                <div 
                                    class="btn-weflix btn-action btn-view" 
                                    @click="viewQuiz(quiz)"
                                    :title="$t('admin.reports.quizzes.viewQuiz')"
                                >
                                    <fa icon="fa-solid fa-eye" />
                                </div>
                                <div 
                                    class="btn-weflix btn-action btn-delete" 
                                    @click="deleteQuiz(quiz)"
                                    :title="$t('admin.reports.quizzes.deleteQuiz')"
                                >
                                    <fa icon="fa-solid fa-trash" />
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <div class="pagination users-pagination">
            <v-responsive>
                <v-pagination
                    :model-value="currentPage + 1"
                    :length="totalPages"
                    :total-visible="5"
                    :disabled="!hasReady"
                    @update:model-value="onPageChange"
                ></v-pagination>
            </v-responsive>
        </div>
    </div>

    <!-- Modal de Visualização do Quiz -->
    <v-dialog v-model="showQuizModal" max-width="60vw" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center quiz-modal-header">
                <div class="quiz-title-section">
                    <h3 class="quiz-title">{{ selectedQuiz?.quizTitle || selectedQuiz?.title || this.$t('admin.reports.quizzes.viewQuizTitle') }}</h3>
                    <p class="quiz-subtitle">{{ $t('admin.reports.quizzes.modalSubtitle') }}</p>
                </div>
                <v-btn icon @click="closeQuizModal" class="close-btn">
                    <fa icon="fa-solid fa-times" />
                </v-btn>
            </v-card-title>
            
            <v-card-text>
                <div v-if="selectedQuiz" class="quiz-details">
                    <div class="quiz-info mb-4">
                        <div class="info-row">
                            <strong>{{ $t('admin.reports.columns.course') }}:</strong> {{ selectedQuiz.courseName || 'N/A' }}
                        </div>
                        <div class="info-row">
                            <strong>{{ $t('admin.reports.columns.student') }}:</strong> {{ selectedQuiz.userName || 'N/A' }}
                        </div>
                        <div class="info-row">
                            <strong>{{ $t('admin.reports.columns.score') }}:</strong> 
                            <v-chip 
                                :color="getScoreColor(selectedQuiz.score)" 
                                size="small"
                                class="ml-2"
                            >
                                {{ selectedQuiz.score ? (selectedQuiz.score / 10).toFixed(1) : 'N/A' }}
                            </v-chip>
                        </div>
                        <div class="info-row">
                            <strong>{{ $t('admin.reports.columns.date') }}:</strong> {{ formatDate(selectedQuiz.dateInsert) }}
                        </div>
                    </div>

                    <!-- Loader enquanto carrega questões -->
                    <div v-if="isLoadingQuiz" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-2">{{ $t('admin.reports.quizzes.loadingQuestions') }}</p>
                    </div>

                    <!-- Questões do Quiz -->
                    <div v-else-if="quizQuestions.length > 0" class="questions-container">
                        <h4 class="mb-3">{{ $t('admin.reports.quizzes.questionsTitle') }}</h4>
                        <div v-for="(question, qIndex) in quizQuestions" :key="question.id" class="question-block">
                            <div class="box_question_quiz review" :class="{ 'editing': isEditingMode }">
                                <div class="number_question_quiz">
                                    <span>{{ $t('admin.reports.quizzes.questionN', { n: qIndex + 1 }) }}</span>
                                </div>
                                <div class="title_question_quiz">
                                    <span v-html="question.description"></span>
                                </div>

                                <div v-for="(option, oIndex) in question.answers" :key="option.id" class="option_question_quiz">
                                    <input
                                        type="radio"
                                        :id="`modal_option${qIndex}_${oIndex}`"
                                        :name="`modal_question_${qIndex}`"
                                        :checked="getStudentAnswer(qIndex) && getStudentAnswer(qIndex).posicao === oIndex"
                                        :disabled="!isEditingMode"
                                        class="hidden-radio"
                                        @change="updateStudentAnswer(qIndex, oIndex, option.id, question.id)"
                                    />

                                    <!-- Ícone de resposta correta -->
                                    <i
                                        class="pi pi-check"
                                        v-if="option.correct"
                                    />

                                    <!-- Ícone de resposta errada -->
                                    <i
                                        class="pi pi-times"
                                        v-if="
                                            getStudentAnswer(qIndex) &&
                                            oIndex === getStudentAnswer(qIndex).posicao &&
                                            !option.correct
                                        "
                                    />

                                    <label
                                        :for="`modal_option${qIndex}_${oIndex}`"
                                        class="option-label"
                                        :class="{ 'editable': isEditingMode }"
                                        @click="isEditingMode && updateStudentAnswer(qIndex, oIndex, option.id, question.id)"
                                    >
                                        <span
                                            :class="[
                                                'option_input',
                                                {
                                                    selected: getStudentAnswer(qIndex) && oIndex === getStudentAnswer(qIndex).posicao,
                                                    correct: option.correct,
                                                    incorrect: !option.correct && getStudentAnswer(qIndex) && oIndex === getStudentAnswer(qIndex).posicao
                                                }
                                            ]"
                                        >{{ optionLabels[oIndex] }}</span>
                                        <span :class="{ selected: getStudentAnswer(qIndex) && oIndex === getStudentAnswer(qIndex).posicao }">
                                            <span v-html="option.description"></span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Mensagem quando não há questões -->
                    <div v-else-if="!isLoadingQuiz" class="no-questions">
                        <p>{{ $t('admin.reports.quizzes.noQuestions') }}</p>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions>
               
              
                <v-spacer></v-spacer>
                <div class="btn-weflix" @click="closeQuizModal">
                    <fa icon="fa-solid fa-times" />
                    <span>{{ $t('admin.reports.quizzes.close') }}</span>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Modal de Confirmação de Exclusão -->
    <v-dialog v-model="showDeleteModal" max-width="320" persistent class="confirm-dialog-delete">
        <v-card>
            <v-card-title class="headline">{{ $t('admin.reports.quizzes.attention') }}</v-card-title>
            
            <v-card-text>
                {{ $t('admin.reports.quizzes.deleteConfirm', { title: quizToDelete?.quizTitle || quizToDelete?.title || $t('admin.reports.fallbacks.noTitle') }) }}
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="cancel" text @click="cancelDelete">{{ $t('admin.reports.quizzes.exit') }}</v-btn>
                <v-btn class="accept" text @click="confirmDelete" :loading="isDeleting">{{ $t('admin.reports.quizzes.delete') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import Loader from '@/components/Loader/loader.vue';
import AdvancedFilters from '@/components/Admin/Reports/AdvancedFilters.vue';
import api from "@/utils/reports.js";
import { mapState } from "vuex";
import { toast } from 'vue3-toastify';

export default {
    data() {
        return {
            label: this.labels,
            userFiltered: [],
            searchTerm: '',
            currentPage: 0,
            pageSize: 10,
            totalPages: 0,
            totalElements: 0,
            alertOpenCourse: false,
            removedcoursename: '',
            afterWidth: 40,
            usersInfo: [],
            openModalCreateWarning: false,
            openModalEditWarning: false,
            hasReady: false,
            indiceItem: [false, false, false, true],
            isMobileIcon: false,
            currentSort: { field: '', order: 'asc' },
            activeFilters: {}, // Armazenar filtros ativos para export
            showQuizModal: false,
            selectedQuiz: null,
            quizQuestions: [],
            studentAnswers: [],
            originalStudentAnswers: [], // Backup das respostas originais
            isEditingMode: false,
            optionLabels: ['A', 'B', 'C', 'D', 'E'],
            showDeleteModal: false,
            quizToDelete: null,
            isDeleting: false,
            isLoadingQuiz: false,
        };
    },
    props: {
        labels: Array
    },
    computed: {
        ...mapState(['token', 'numberIntensTable']),
        paginatedQuizzes() {
            // Retornar todos os dados da página atual (já vem paginado da API)
            return this.userFiltered || [];
        },
    },
    methods: {
        getScoreColor(score) {
            if (!score) return 'grey';
            if (score >= 70) return 'success';
            if (score >= 50) return 'warning';
            return 'error';
        },
        async exportToCSV() {
            try {
                toast.info(this.$t('admin.reports.exporting'));
                
                // Preparar filtros para a API de export
                const exportFilters = {};
                
                if (this.activeFilters.courseName) {
                    exportFilters.courseName = this.activeFilters.courseName;
                }
                if (this.activeFilters.userName) {
                    exportFilters.userName = this.activeFilters.userName;
                }
                if (this.activeFilters.score) {
                    exportFilters.gradeFilter = this.activeFilters.score;
                }
                if (this.activeFilters.startDate) {
                    exportFilters.startDate = this.activeFilters.startDate;
                }
                if (this.activeFilters.endDate) {
                    exportFilters.endDate = this.activeFilters.endDate;
                }
                
                // Chamar endpoint de export
                const response = await api.getDataQuizExport(this.token, exportFilters);
                
                // Fazer download do arquivo blob
                const url = window.URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_quizzes.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(this.$t('admin.reports.exportSuccess'));
            } catch (error) {
                toast.error(this.$t('admin.reports.exportError'));
                console.error('Erro ao exportar CSV:', error);
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
        },
        async getDataReport(searchTerm = '') {
            try {
                this.hasReady = false;
                
                const filters = {};
                
                // Se houver searchTerm, buscar por título do quiz ou nome do aluno
                if (searchTerm && searchTerm.trim() !== '') {
                    filters.userName = searchTerm.trim();
                }
                
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                console.log('quizes.vue - buscando dados com filtros:', filters, 'pagination:', pagination);
                
                const response = await api.getDataQuiz(this.token, filters, pagination);
                console.log('quizes.vue - resposta da API:', response);
                
                // Processar resposta (mesmo que vazia) e mapear quizTitle para title e grade para score
                let processedData = (response?.content || []).map(quiz => ({
                    ...quiz,
                    title: quiz.quizTitle || quiz.title || this.$t('admin.reports.fallbacks.noTitle'),
                    score: quiz.grade !== undefined ? (quiz.grade * 10) : (quiz.score || 0),
                    // Preservar tokenProgress, idProgressToken e idProgress para uso no viewQuiz
                    tokenProgress: quiz.tokenProgress || quiz.idProgressToken || quiz.idProgress,
                    idProgressToken: quiz.idProgressToken,
                    idProgress: quiz.idProgress || quiz.idProgressToken
                }));
                
                // Ordenar por data (mais recentes primeiro) por padrão
                processedData.sort((a, b) => {
                    const dateA = new Date(a.dateInsert || 0);
                    const dateB = new Date(b.dateInsert || 0);
                    return dateB - dateA; // Mais recentes primeiro
                });
                
                this.usersInfo = processedData;
                this.userFiltered = processedData;
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                
                this.hasReady = true;
                
                // Mostrar mensagem se não houver resultados
                if (this.userFiltered.length === 0 && searchTerm) {
                    toast.info(this.$t('admin.reports.noSearchResults'));
                }
            } catch (error) {
                toast.error(this.$t('admin.reports.loadError'));
                console.error('Erro Report Quizzes', error);
                // Garantir que mesmo em caso de erro, os dados sejam limpos
                this.usersInfo = [];
                this.userFiltered = [];
                this.totalPages = 0;
                this.totalElements = 0;
                this.hasReady = true;
            }
        },
        filteredQuizzes() {
            if (this.searchTerm.trim() === '') {
                this.userFiltered = this.usersInfo;
            } else {
                this.userFiltered = this.usersInfo.filter((quiz) => {
                    const title = quiz.quizTitle || quiz.title || '';
                    return title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                        (quiz.courseName && quiz.courseName.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
                        (quiz.userName && quiz.userName.toLowerCase().includes(this.searchTerm.toLowerCase()));
                });
            }
            return this.userFiltered;
        },
        async onPageChange(page) {
            // Converter de 1-based (UI) para 0-based (API)
            this.currentPage = page - 1;
            await this.getDataReport();
        },
        removecourse(id, name) {
            this.removedcoursename = name;
            this.alertOpenCourse = !this.alertOpenCourse;
            setTimeout(() => {
                this.alertOpenCourse = !this.alertOpenCourse;
                this.removedcoursename = '';
            }, 4000);
        },
        edit(id) {
            this.openModalEditWarning = !this.openModalEditWarning;
        },
        create() {
            this.openModalCreateWarning = !this.openModalCreateWarning;
        },
        updatevalueCreate(newvalue) {
            this.openModalCreateWarning = newvalue;
        },
        updatevalueEdit(newvalue) {
            this.openModalEditWarning = newvalue;
        },
        filterByDate(period, index) {
            this.indiceItem = this.indiceItem.map((item, i) => i === index ? true : false);
            const now = new Date();
            let startDate;

            switch (period) {
                case 'today':
                    startDate = new Date(now.setHours(0, 0, 0, 0));
                    break;
                case 'last30days':
                    startDate = new Date(now.setDate(now.getDate() - 30));
                    break;
                case 'last90days':
                    startDate = new Date(now.setDate(now.getDate() - 90));
                    break;
                case 'alltime':
                    this.userFiltered = this.usersInfo;
                    return;
            }
            this.userFiltered = this.usersInfo.filter((quiz) => {
                const quizDate = new Date(quiz.dateInsert);
                return quizDate >= startDate;
            });
        },
        filterByCustomDate(event) {
            const selectedDate = new Date(event.target.value);
            if (!selectedDate) {
                this.userFiltered = this.usersInfo;
            }
            this.userFiltered = this.usersInfo.filter((quiz) => {
                const quizDate = new Date(quiz.dateInsert);
                return quizDate >= selectedDate;
            });
        },
        onFiltersApplied(filters) {
            this.applyAdvancedFilters(filters);
        },
        onFiltersCleared() {
            this.clearAdvancedFilters();
        },
        async applyAdvancedFilters(filters) {
            try {
                this.hasReady = false;
                
                // Armazenar filtros ativos para export
                this.activeFilters = { ...filters };
                if (filters.dateFrom) this.activeFilters.startDate = filters.dateFrom;
                if (filters.dateTo) this.activeFilters.endDate = filters.dateTo;
                if (filters.score) this.activeFilters.score = filters.score;
                
                // Resetar para primeira página ao aplicar filtros
                this.currentPage = 0;
                
                // Preparar filtros para a API
                const apiFilters = {};
                
                // Filtro por nome do curso
                if (filters.courseName && filters.courseName.trim() !== '') {
                    apiFilters.courseName = filters.courseName.trim();
                }
                
                // Filtro por nome do usuário/aluno
                if (filters.userName && filters.userName.trim() !== '') {
                    apiFilters.userName = filters.userName.trim();
                }
                
                // Filtro por data
                if (filters.dateFrom) {
                    apiFilters.startDate = filters.dateFrom;
                }
                if (filters.dateTo) {
                    apiFilters.endDate = filters.dateTo;
                }
                
                // Filtro por nota/score - usar gradeFilter para a API
                // Pode ser um intervalo (ex: "0-2", "2.5-4") ou status ("aprovado", "reprovado")
                if (filters.score) {
                    apiFilters.gradeFilter = filters.score;
                }
                
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                // Buscar dados da API com os filtros
                console.log('Aplicando filtros via API (quizzes):', apiFilters, 'pagination:', pagination);
                const response = await api.getDataQuiz(this.token, apiFilters, pagination);
                
                // Processar resposta da API (mesmo que vazia) e mapear quizTitle para title e grade para score
                let filteredData = (response?.content || []).map(quiz => ({
                    ...quiz,
                    title: quiz.quizTitle || quiz.title || this.$t('admin.reports.fallbacks.noTitle'),
                    score: quiz.grade !== undefined ? (quiz.grade * 10) : (quiz.score || 0),
                    // Preservar tokenProgress, idProgressToken e idProgress para uso no viewQuiz
                    tokenProgress: quiz.tokenProgress || quiz.idProgressToken || quiz.idProgress,
                    idProgressToken: quiz.idProgressToken,
                    idProgress: quiz.idProgress || quiz.idProgressToken
                }));
                
                // Ordenar por data (mais recentes primeiro) quando filtros de data são aplicados
                if (filters.dateFrom || filters.dateTo) {
                    filteredData.sort((a, b) => {
                        const dateA = new Date(a.dateInsert || 0);
                        const dateB = new Date(b.dateInsert || 0);
                        return dateB - dateA; // Mais recentes primeiro
                    });
                }
                
                this.usersInfo = filteredData; // Atualizar dados base
                
                this.userFiltered = filteredData;
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                
                this.hasReady = true;
                
                // Mostrar mensagem se não houver resultados
                if (filteredData.length === 0) {
                    toast.info(this.$t('admin.reports.noFilterResults'));
                }
                
            } catch (error) {
                toast.error(this.$t('admin.reports.filtersApplyError'));
                console.error('Erro ao aplicar filtros:', error);
                // Garantir que mesmo em caso de erro, os dados sejam limpos
                this.userFiltered = [];
                this.usersInfo = [];
                this.hasReady = true;
            }
        },
        async clearAdvancedFilters() {
            try {
                this.hasReady = false;
                this.currentPage = 0;
                
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                // Buscar todos os dados novamente sem filtros
                const response = await api.getDataQuiz(this.token, {}, pagination);
                
                // Mapear quizTitle para title e grade para score
                let processedData = (response?.content || []).map(quiz => ({
                    ...quiz,
                    title: quiz.quizTitle || quiz.title || this.$t('admin.reports.fallbacks.noTitle'),
                    score: quiz.grade !== undefined ? (quiz.grade * 10) : (quiz.score || 0),
                    // Preservar tokenProgress, idProgressToken e idProgress para uso no viewQuiz
                    tokenProgress: quiz.tokenProgress || quiz.idProgressToken || quiz.idProgress,
                    idProgressToken: quiz.idProgressToken,
                    idProgress: quiz.idProgress || quiz.idProgressToken
                }));
                
                // Ordenar por data (mais recentes primeiro) por padrão
                processedData.sort((a, b) => {
                    const dateA = new Date(a.dateInsert || 0);
                    const dateB = new Date(b.dateInsert || 0);
                    return dateB - dateA; // Mais recentes primeiro
                });
                
                this.usersInfo = processedData;
                this.userFiltered = processedData;
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                this.hasReady = true;
            } catch (error) {
                console.error('Erro ao limpar filtros:', error);
                this.userFiltered = [];
                this.usersInfo = [];
                this.hasReady = true;
            }
        },
        async viewQuiz(quiz) {
            try {
                this.selectedQuiz = quiz;
                this.showQuizModal = true;
                this.isLoadingQuiz = true;
                this.quizQuestions = [];
                this.studentAnswers = [];
                
                // Obter tokenProgress do quiz (pode ser idProgressToken, tokenProgress ou idProgress)
                const tokenProgress = quiz.tokenProgress || quiz.idProgressToken || quiz.idProgress;
                
                if (!tokenProgress) {
                    toast.error(this.$t('admin.reports.quizzes.progressTokenMissing'));
                    console.error('Quiz sem tokenProgress:', quiz);
                    this.quizQuestions = [];
                    this.studentAnswers = [];
                    this.isLoadingQuiz = false;
                    return;
                }
                
                // Buscar questões e respostas do quiz via API
                const quizData = await api.getDataQuizAnswer(this.token, tokenProgress);
                
                console.log('Dados retornados da API:', quizData);
                
                // Processar dados retornados pela API
                // A API retorna um array de objetos onde cada objeto representa uma alternativa de uma questão
                // Precisamos agrupar por orderQuestion e orderAnswer
                const dataArray = quizData?.data || quizData || [];
                
                console.log('Array de dados processado:', dataArray);
                console.log('Tamanho do array:', dataArray.length);
                
                if (dataArray.length === 0) {
                    this.quizQuestions = [];
                    this.studentAnswers = [];
                    toast.info(this.$t('admin.reports.quizzes.noQuestionsFound'));
                    return;
                }
                
                // Agrupar dados por orderQuestion
                const questionsMap = {};
                const studentAnswersMap = {};
                
                dataArray.forEach((item) => {
                    const questionOrder = item.orderQuestion;
                    const answerOrder = item.orderAnswer;
                    
                    // Criar ou atualizar a questão
                    if (!questionsMap[questionOrder]) {
                        questionsMap[questionOrder] = {
                            id: questionOrder,
                            description: item.questionDescription || '',
                            answers: []
                        };
                    }
                    
                    // Adicionar alternativa à questão
                    const answer = {
                        id: answerOrder,
                        description: item.answerDescription || '',
                        correct: item.isCorrect === 1,
                        selected: item.selectedByUser === 1 || item.selectedByUser === true
                    };
                    
                    questionsMap[questionOrder].answers.push(answer);
                    
                    // Se a alternativa foi selecionada pelo usuário, registrar a resposta
                    // selectedByUser deve ser 1 ou true para indicar seleção (null = não selecionado)
                    if (item.selectedByUser === 1 || item.selectedByUser === true) {
                        // Usar o índice baseado na posição no array (orderAnswer - 1)
                        // Mas precisamos encontrar a posição correta no array ordenado
                        const currentAnswers = questionsMap[questionOrder].answers;
                        const answerIndex = currentAnswers.length - 1; // Índice atual no array
                        
                        studentAnswersMap[questionOrder] = {
                            posicao: answerIndex, // Índice no array de respostas
                            questao: questionOrder,
                            id: answerOrder
                        };
                    }
                });
                
                // Converter o mapa em array de questões ordenadas
                this.quizQuestions = Object.keys(questionsMap)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map(key => {
                        const question = questionsMap[key];
                        // Ordenar alternativas por orderAnswer (id)
                        question.answers.sort((a, b) => a.id - b.id);
                        return question;
                    });
                
                // Recalcular posições das respostas após ordenar as alternativas
                // Agora que as alternativas estão ordenadas, precisamos encontrar a posição correta
                Object.keys(studentAnswersMap).forEach(questionKey => {
                    const question = questionsMap[questionKey];
                    const answerId = studentAnswersMap[questionKey].id;
                    // Encontrar o índice da resposta selecionada no array ordenado
                    const answerIndex = question.answers.findIndex(a => a.id === answerId);
                    if (answerIndex !== -1) {
                        studentAnswersMap[questionKey].posicao = answerIndex;
                    }
                });
                
                // Converter o mapa de respostas em array ordenado por questão
                const sortedAnswers = Object.keys(studentAnswersMap)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map(key => studentAnswersMap[key]);
                
                // Garantir que temos um array de respostas com o mesmo tamanho das questões
                // Preencher com null para questões sem resposta
                const answersArray = [];
                this.quizQuestions.forEach((question, index) => {
                    const answer = sortedAnswers.find(a => a.questao === question.id);
                    answersArray[index] = answer || null;
                });
                this.studentAnswers = answersArray;
                
                console.log('Questões processadas:', this.quizQuestions);
                console.log('Respostas processadas:', this.studentAnswers);
                console.log('Número de questões:', this.quizQuestions.length);
                
                this.isLoadingQuiz = false;
                
            } catch (error) {
                toast.error(this.$t('admin.reports.quizzes.loadDetailsError'));
                console.error('Erro ao visualizar quiz:', error);
                console.error('Stack trace:', error.stack);
                this.quizQuestions = [];
                this.studentAnswers = [];
                this.isLoadingQuiz = false;
            }
        },
        closeQuizModal() {
            this.showQuizModal = false;
            this.selectedQuiz = null;
            this.quizQuestions = [];
            this.studentAnswers = [];
            this.originalStudentAnswers = [];
            this.isEditingMode = false;
        },
        toggleEditMode() {
            if (this.isEditingMode) {
                this.saveChanges();
            } else {
                this.startEditMode();
            }
        },
        startEditMode() {
            // Fazer backup das respostas originais
            this.originalStudentAnswers = JSON.parse(JSON.stringify(this.studentAnswers));
            this.isEditingMode = true;
            toast.info(this.$t('admin.reports.quizzes.editModeOn'));
        },
        cancelEdit() {
            // Restaurar respostas originais
            this.studentAnswers = JSON.parse(JSON.stringify(this.originalStudentAnswers));
            this.isEditingMode = false;
            toast.info(this.$t('admin.reports.quizzes.editModeOff'));
        },
        saveChanges() {
            // Aqui você pode implementar a chamada para a API para salvar as alterações
            console.log('Salvando alterações:', this.studentAnswers);
            this.isEditingMode = false;
            toast.success(this.$t('admin.reports.quizzes.answersUpdated'));
            
            // Atualizar a nota do quiz baseada nas novas respostas
            this.updateQuizScore();
        },
        updateStudentAnswer(questionIndex, optionIndex, optionId, questionId) {
            if (!this.isEditingMode) return;
            
            // Atualizar a resposta do aluno
            const updatedAnswers = [...this.studentAnswers];
            updatedAnswers[questionIndex] = {
                posicao: optionIndex,
                questao: questionId,
                id: optionId
            };
            this.studentAnswers = updatedAnswers;
        },
        updateQuizScore() {
            if (!this.selectedQuiz) return;
            
            // Calcular nova nota baseada nas respostas alteradas
            let correctAnswers = 0;
            this.quizQuestions.forEach((question, qIndex) => {
                const studentAnswer = this.studentAnswers[qIndex];
                if (studentAnswer) {
                    const correctOption = question.answers.find(opt => opt.correct);
                    if (correctOption && studentAnswer.id === correctOption.id) {
                        correctAnswers++;
                    }
                }
            });
            
            const newScore = Math.round((correctAnswers / this.quizQuestions.length) * 100);
            this.selectedQuiz.score = newScore;
            
            // Atualizar também na lista principal
            const quizIndex = this.usersInfo.findIndex(q => q.id === this.selectedQuiz.id);
            if (quizIndex !== -1) {
                this.usersInfo[quizIndex].score = newScore;
                this.userFiltered = [...this.usersInfo];
            }
            
            toast.success(this.$t('admin.reports.quizzes.scoreUpdated', { score: (newScore / 10).toFixed(1) }));
        },
        getStudentAnswer(questionIndex) {
            if (!this.studentAnswers || this.studentAnswers.length === 0) {
                return null;
            }
            return this.studentAnswers[questionIndex] || null;
        },
        getMockQuizQuestions(quizId) {
            // Mock das questões - substitua pela chamada real da API
            const questionsMap = {
                1: [
                    {
                        id: 1,
                        description: "Qual é a fórmula da área do círculo?",
                        answers: [
                            { id: 1, description: "π × r²", correct: true },
                            { id: 2, description: "2 × π × r", correct: false },
                            { id: 3, description: "π × d", correct: false },
                            { id: 4, description: "r²", correct: false }
                        ]
                    },
                    {
                        id: 2,
                        description: "Quanto é 5 + 3 × 2?",
                        answers: [
                            { id: 5, description: "16", correct: false },
                            { id: 6, description: "11", correct: true },
                            { id: 7, description: "13", correct: false },
                            { id: 8, description: "10", correct: false }
                        ]
                    }
                ],
                2: [
                    {
                        id: 3,
                        description: "Qual é o plural de 'cidadão'?",
                        answers: [
                            { id: 9, description: "cidadãos", correct: true },
                            { id: 10, description: "cidadões", correct: false },
                            { id: 11, description: "cidadães", correct: false },
                            { id: 12, description: "cidadãos", correct: false }
                        ]
                    }
                ]
            };
            return questionsMap[quizId] || [];
        },
        getMockStudentAnswers(quizId) {
            // Mock das respostas do aluno - substitua pela chamada real da API
            const answersMap = {
                1: [
                    { posicao: 0, questao: 1, id: 1 }, // Resposta correta
                    { posicao: 1, questao: 2, id: 6 }  // Resposta correta
                ],
                2: [
                    { posicao: 0, questao: 3, id: 9 }  // Resposta correta
                ]
            };
            return answersMap[quizId] || [];
        },
        deleteQuiz(quiz) {
            this.quizToDelete = quiz;
            this.showDeleteModal = true;
        },
        cancelDelete() {
            this.showDeleteModal = false;
            this.quizToDelete = null;
        },
        async confirmDelete() {
            if (!this.quizToDelete) return;
            
            try {
                this.isDeleting = true;
                
                // Obter tokenProgress do quiz para deletar
                const tokenProgress = this.quizToDelete.tokenProgress || this.quizToDelete.idProgressToken || this.quizToDelete.idProgress;
                
                if (!tokenProgress) {
                    toast.error(this.$t('admin.reports.quizzes.deleteIdMissing'));
                    this.showDeleteModal = false;
                    this.quizToDelete = null;
                    return;
                }
                
                // Chamar API para deletar o progresso do quiz
                await api.deleteQuizProgress(this.token, tokenProgress);
                
                toast.success(`Quiz "${this.quizToDelete.quizTitle || this.quizToDelete.title || this.$t('admin.reports.fallbacks.noTitle')}" excluído com sucesso!`);
                
                this.showDeleteModal = false;
                this.quizToDelete = null;
                
                // Recarregar dados mantendo os filtros ativos
                if (Object.keys(this.activeFilters).length > 0) {
                    // Se há filtros ativos, reaplicar os filtros
                    await this.applyAdvancedFilters(this.activeFilters);
                } else {
                    // Caso contrário, recarregar dados sem filtros
                    await this.getDataReport();
                }
                
            } catch (error) {
                toast.error(this.$t('admin.reports.quizzes.deleteError'));
                console.error('Erro ao excluir quiz:', error);
            } finally {
                this.isDeleting = false;
            }
        },
        sortBy(field) {
            if (this.currentSort.field === field) {
                this.currentSort.order = this.currentSort.order === 'asc' ? 'desc' : 'asc';
            } else {
                this.currentSort.field = field;
                this.currentSort.order = 'asc';
            }
            
            this.userFiltered = this.sortData([...this.userFiltered], field, this.currentSort.order);
        },
        sortData(data, sortBy, sortOrder) {
            return data.sort((a, b) => {
                let aValue, bValue;
                
                switch (sortBy) {
                    case 'title':
                        aValue = ((a.quizTitle || a.title) || '').toLowerCase();
                        bValue = ((b.quizTitle || b.title) || '').toLowerCase();
                        break;
                    case 'courseName':
                        aValue = (a.courseName || '').toLowerCase();
                        bValue = (b.courseName || '').toLowerCase();
                        break;
                    case 'userName':
                        aValue = (a.userName || '').toLowerCase();
                        bValue = (b.userName || '').toLowerCase();
                        break;
                    case 'score':
                        aValue = parseInt(a.score) || 0;
                        bValue = parseInt(b.score) || 0;
                        break;
                    case 'date':
                        aValue = new Date(a.dateInsert);
                        bValue = new Date(b.dateInsert);
                        break;
                    default:
                        return 0;
                }
                
                if (sortOrder === 'asc') {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });
        },
    },
    components: {
        Loader,
        AdvancedFilters
    },
    async created() {
        await this.getDataReport();
    },
};
</script>

<style scoped>
.container {
    max-width: 100%;
    padding: 0 !important;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}
.search-form-input {
    position: relative;
    width: 100%;
}
.title-container {
    position: relative;
    margin-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
}

.title-container .title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 0.5rem;
}

.title-container .subtitle {
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.4;
    margin-bottom: 1rem;
}

.search-row {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
}

.search-row .form-group {
    flex: 1;
}

.export-button-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
    margin-top: 1rem;
}

.export-button-container .btn-weflix {
    background-color: #2f2e8b;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
    border: none;
}

.export-button-container .btn-weflix:hover:not(:disabled) {
    background-color: #23225f;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(47, 46, 139, 0.3);
}

.export-button-container .btn-weflix:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.export-button-container .btn-weflix svg {
    font-size: 1rem;
}

.btn-weflix.absolute {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #2f2e8b;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
}

.btn-weflix.absolute:hover {
    background-color: #23225f;
    transform: translateY(-2px);
}

.users-table-responsive {
    background-color: #ffffff;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.users-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.users-table th {
    background-color: #f8fafc;
    padding: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    text-align: left;
    border-bottom: 2px solid #e2e8f0;
}

.users-table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-bottom: 1px solid #f0f0f0;
    color: #4a5568;
}

.users-table tr:hover {
    background-color: #f8fafc;
}

.badge-active, .badge-inactive {
    padding: 0.25rem 0.75rem;
    border-radius: 5px;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-block;
    text-align: center;
    transition: all 0.2s ease;
    width: fit-content;
}

.badge-active {
    background-color: #7bcc8180;
    color: #4b614e;
}

.badge-inactive {
    background-color: #d7d7ee80;
    color: #2f2e8b;
}

.dark .badge-active, .dark .badge-inactive {
    color: #fff;
}

.user-btn-edit {
    padding: 0.4rem;
    margin: 0 0.25rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: #2f2e8b;
}

.search-form-input {
    position: relative;
}

.search-form-control {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.search-form-control:focus {
    outline: none;
    border-color: #2f2e8b;
    box-shadow: 0 0 0 3px rgba(47, 46, 139, 0.1);
}

.search-input-icon {
    position: absolute;
    right: 0.75rem;
    top: 45%;
    transform: translateY(-50%);
    color: #9ca3af;
    cursor: pointer;
}

.users-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-bottom: 10px;
}

.users-page-btn {
    padding: 0.5rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: white;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s;
}

.users-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.users-page-btn:not(:disabled):hover {
    background: #f8fafc;
    border-color: #cbd5e0;
}

.users-page-info {
    color: #4a5568;
    font-size: 0.875rem;
}

@media (max-width: 768px) {
    .container {
        padding: 1rem;
    }

    .title-container {
        margin-bottom: 1.5rem;
    }

    .search-row {
        flex-direction: column;
    }

    .users-table th, .users-table td {
        padding: 0.5rem;
    }
}

@media (max-width: 600px) {
    .btn-weflix.absolute {
        padding: 0.5rem;
    }
    
    .btn-weflix.absolute span {
        display: none;
    }

    .users-table-responsive {
        overflow-x: auto;
    }
}
.content.dashboard > .container {
    padding: 20px;
    padding-bottom: 35px;
    box-shadow: 0px 6px 10px 0px #dfdfdf;
    min-height: 89vh;
    padding-bottom: 0;
}
select.form-control.filter-form-control {
    width: 60%;
}
.form-group.filter-form-group {
    justify-content: space-around;
    align-items: center;
    margin: 0 !important;
}
.form-group.search-form-group {
    margin: 0 !important;
}
table.table.users-table {
    border: none !important;
    padding: 0 !important;
}
button.btn-edit.user-btn-edit svg {
    color: #2f2e8b;
}
.form-group.filter-form-group {
    display: flex;
}
.btn-weflix.absolute span.flex {
    font-size: 16px;
}
.btn-weflix.absolute svg {
    color: white;
    font-size: 15px;
}
/* DARK */
.dashboard.dark .users-table-responsive {
    background-color: #000 !important;
}
.dashboard.dark .weeducAdminReports .page-info.users-page-info {
    color: white !important;
}
.dashboard.dark .weeducAdminReports .users-table th {
    border-bottom: 2px solid #575757 !important;
}
.dashboard.dark .weeducAdminReports .users-table td {
    border-bottom: 2px solid #575757 !important;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    position: relative;
}

.sortable-header:hover {
    background-color: #f1f3f4;
}

.sort-icon {
    margin-left: 0.5rem;
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.sortable-header:hover .sort-icon {
    opacity: 1;
}

.dashboard.dark .sortable-header:hover {
    background-color: #666;
}

.table-with-filters {
    border-radius: 0 0 6px 6px;
    border: 1px solid #e2e8f0;
    border-top: none;
    overflow: hidden;
}

.dashboard.dark .table-with-filters {
    border-color: #444;
}


/* Botões de ação */
.action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    align-items: center;
}

.btn-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    font-size: 14px;
    padding: 0;
    min-width: 32px;
}

.btn-view {
    background-color: #e3f2fd;
    color: #1976d2;
}

.btn-view:hover {
    background-color: #bbdefb;
    transform: translateY(-1px);
}

.btn-delete {
    background-color: #ffebee;
    color: #d32f2f;
}

.btn-delete:hover {
    background-color: #ffcdd2;
    transform: translateY(-1px);
}

.btn-cancel {
    background-color: #f5f5f5;
    color: #666;
}

.btn-cancel:hover {
    background-color: #e0e0e0;
    transform: translateY(-1px);
}

.dashboard.dark .btn-view {
    background-color: #1e3a8a;
    color: #93c5fd;
}

.dashboard.dark .btn-view:hover {
    background-color: #1e40af;
}

.dashboard.dark .btn-delete {
    background-color: #7f1d1d;
    color: #fca5a5;
}

.dashboard.dark .btn-delete:hover {
    background-color: #991b1b;
}

.dashboard.dark .btn-cancel {
    background-color: #444;
    color: #ccc;
}

.dashboard.dark .btn-cancel:hover {
    background-color: #555;
}

/* Estilos do Modal de Quiz */
.quiz-modal-header {
    background: linear-gradient(135deg, #2f2e8b 0%, #1e1e5f 100%);
    color: white;
    padding: 1.5rem;
    border-bottom: none;
}

.quiz-title-section {
    flex: 1;
}

.quiz-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: white;
}

.quiz-subtitle {
    font-size: 0.9rem;
    opacity: 0.9;
    margin: 0.25rem 0 0 0;
    color: white;
}

.close-btn {
    color: white !important;
    background-color: rgba(255, 255, 255, 0.1) !important;
    transition: all 0.3s ease;
}

.close-btn:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
    transform: scale(1.1);
}

.quiz-details {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 0.5rem;
    /* Ocultar barra de rolagem */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.quiz-details::-webkit-scrollbar {
    display: none; /* WebKit */
}

/* Melhorar o layout do modal */
.v-dialog .v-card {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.v-dialog .v-card .v-card-text {
    flex: 1;
    overflow: hidden;
    padding: 1.5rem;
}

.v-dialog .v-card .v-card-actions {
    flex-shrink: 0;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e0e0e0;
    background-color: #fafafa;
}

/* Responsividade para telas menores */
@media (max-width: 1200px) {
    .v-dialog {
        max-width: 80vw !important;
    }
}

@media (max-width: 768px) {
    .v-dialog {
        max-width: 95vw !important;
        margin: 1rem;
    }
    
    .quiz-details {
        max-height: 50vh;
    }
    
    .v-dialog .v-card .v-card-text {
        padding: 1rem;
    }
    
    .v-dialog .v-card .v-card-actions {
        padding: 0.75rem 1rem;
    }
}

.quiz-info {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.info-row {
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
}

.info-row:last-child {
    margin-bottom: 0;
}

.questions-container {
    margin-top: 1.5rem;
}

.question-block {
    margin-bottom: 2rem;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.question-block:last-child {
    margin-bottom: 0;
}

.box_question_quiz {
    border: none;
    border-radius: 0;
    padding: 1.5rem;
    background-color: #ffffff;
    position: relative;
    margin: 0;
}

.box_question_quiz.review {
    background-color: #fafafa;
}

.number_question_quiz {
    font-weight: bold;
    color: #2f2e8b;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
}

.title_question_quiz {
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 1.4;
}

.option_question_quiz {
    margin-bottom: 1rem;
    position: relative;
    padding: 0.5rem;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.option_question_quiz:hover {
    background-color: #f8f9fa;
}

.hidden-radio {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.option-label {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.5rem;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.option-label:hover {
    background-color: #f5f5f5;
}

.option-label.editable {
    cursor: pointer;
    transition: all 0.2s ease;
}

.option-label.editable:hover {
    background-color: #e3f2fd;
    transform: translateX(2px);
}

.option_input {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 2px solid #ddd;
    border-radius: 50%;
    margin-right: 0.75rem;
    font-weight: bold;
    font-size: 0.8rem;
    transition: all 0.2s ease;
    background-color: #fff;
}

.option_input.selected {
    background-color: #2f2e8b;
    color: white;
    border-color: #2f2e8b;
}

.option_input.correct {
    background-color: #4caf50;
    color: white;
    border-color: #4caf50;
}

.option_input.incorrect {
    background-color: #f44336;
    color: white;
    border-color: #f44336;
}

.pi-check {
    color: #4caf50;
    font-size: 1.2rem;
    margin-right: 0.5rem;
}

.pi-times {
    color: #f44336;
    font-size: 1.2rem;
    margin-right: 0.5rem;
}

.no-questions {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
}

/* Modo escuro para o modal */
.dashboard.dark .quiz-modal-header,
.dark .quiz-modal-header {
    background: linear-gradient(135deg, #1a1a4a 0%, #0f0f2a 100%);
}

.dashboard.dark .quiz-info,
.dark .quiz-info {
    background-color: #2d2d2d !important;
    border-color: #444 !important;
    color: white !important;
}

.dashboard.dark .box_question_quiz,
.dark .box_question_quiz {
    background-color: #2d2d2d !important;
    border-color: #444 !important;
    color: white !important;
}

.dashboard.dark .box_question_quiz.review,
.dark .box_question_quiz.review {
    background-color: #2d2d2d !important;
}

.dashboard.dark .option-label:hover {
    background-color: #333;
}

.dashboard.dark .option_input {
    background-color: #333;
    border-color: #555;
    color: white;
}

.dashboard.dark .option_input.selected {
    background-color: #2f2e8b;
    border-color: #2f2e8b;
}

.dashboard.dark .no-questions {
    color: #ccc;
}

/* Melhorias do modo escuro para questões do quiz */
.dashboard.dark .question-block,
.dark .question-block {
    background-color: #2d2d2d !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.dashboard.dark .number_question_quiz,
.dark .number_question_quiz {
    color: #93c5fd;
}

.dashboard.dark .title_question_quiz,
.dark .title_question_quiz {
    color: #e5e7eb;
}

.dashboard.dark .option_question_quiz,
.dark .option_question_quiz {
    background-color: transparent;
}

.dashboard.dark .option_question_quiz:hover,
.dark .option_question_quiz:hover {
    background-color: #374151;
}

.dashboard.dark .option-label,
.dark .option-label {
    color: #e5e7eb;
}

.dashboard.dark .option-label:hover,
.dark .option-label:hover {
    background-color: #374151;
}

.dashboard.dark .option-label.editable:hover,
.dark .option-label.editable:hover {
    background-color: #1e3a8a;
    color: white;
}

.dashboard.dark .option_input,
.dark .option_input {
    background-color: #374151;
    border-color: #6b7280;
    color: #e5e7eb;
}

.dashboard.dark .option_input.selected,
.dark .option_input.selected {
    background-color: #2f2e8b;
    border-color: #2f2e8b;
    color: white;
}

.dashboard.dark .option_input.correct,
.dark .option_input.correct {
    background-color: #059669;
    border-color: #059669;
    color: white;
}

.dashboard.dark .option_input.incorrect,
.dark .option_input.incorrect {
    background-color: #dc2626;
    border-color: #dc2626;
    color: white;
}

.dashboard.dark .pi-check,
.dark .pi-check {
    color: #10b981;
}

.dashboard.dark .pi-times,
.dark .pi-times {
    color: #ef4444;
}

.dashboard.dark .questions-container h4,
.dark .questions-container h4 {
    color: #e5e7eb;
}

.dashboard.dark .quiz-details,
.dark .quiz-details {
    background-color: #111827;
}

.dashboard.dark .v-card-text,
.dark .v-card-text {
    background-color: #111827;
}

.dashboard.dark .v-card-actions,
.dark .v-card-actions {
    background-color: #111827 !important;
    border-top: 1px solid #444 !important;
}

.dashboard.dark .v-card-actions .btn-weflix,
.dark .v-card-actions .btn-weflix {
    background-color: #2f2e8b !important;
    color: white !important;
    border: none !important;
}

.dashboard.dark .v-card-actions .btn-weflix:hover,
.dark .v-card-actions .btn-weflix:hover {
    background-color: #23225f !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(47, 46, 139, 0.3);
}

/* Estilos para botões do modal */
.v-card-actions .btn-weflix {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
    background-color: #2f2e8b;
    color: white;
    border: none;
    font-size: 14px;
}

.v-card-actions .btn-weflix:hover {
    background-color: #23225f;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(47, 46, 139, 0.3);
}

.v-card-actions .btn-weflix.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.v-card-actions .btn-cancel {
    background-color: #6c757d;
    color: white;
}

.v-card-actions .btn-cancel:hover {
    background-color: #5a6268;
}

.dashboard.dark .v-card-actions .btn-weflix {
    background-color: #2f2e8b;
    color: white;
}

.dashboard.dark .v-card-actions .btn-weflix:hover {
    background-color: #23225f;
}

.dashboard.dark .v-card-actions .btn-cancel {
    background-color: #495057;
    color: white;
}

.dashboard.dark .v-card-actions .btn-cancel:hover {
    background-color: #343a40;
}

/* Estilos para modo de edição */
.box_question_quiz.editing {
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
}

.dashboard.dark .option-label.editable:hover {
    background-color: #1e3a8a;
    color: white;
}

/* Estilos do Modal de Confirmação de Exclusão */
.confirm-dialog-delete .v-card.v-theme--light.v-card--density-default.v-card--variant-elevated {
    padding: 0 !important;
}

.confirm-dialog-delete .v-card-title.headline {
    padding: 1rem 1.5rem 0.5rem 1.5rem !important;
}

</style>
